'use client';

import { Bed, CoffeeCup, Star, StarHalfDashed, ThreeStars } from '@byway/icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { Key } from 'react-aria-components';
import { AriaSelect, SelectItem } from '~/components/AriaSelect';
import { Button } from '~/components/Button';
import { CenteredModal } from '~/components/CenteredModal';
import { Divider } from '~/components/Divider';
import { OverlayDrawer } from '~/components/Drawer';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import type { SortKeys } from '~/components/RoomSelectionPopup/src/constants';
import { sortAndFilterRooms } from '~/components/RoomSelectionPopup/src/utils';
import { ToggleButton } from '~/components/ToggleButton';
import { ToggleButtonGroup } from '~/components/ToggleButtonGroup';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { useToast } from '~/hooks/useToasts';
import { api } from '~/libs/api';
import type { Room } from '~/schemas/trip';
import { expandedBoardBasis, sanitzeBoardBasis } from '~/utils/accommodationHelpers';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { logError } from '~/utils/logger';
import { aggregatePax, pricePerPerson as getPricePerPerson } from '~/utils/paxHelpers/paxHelper';
import { clearStayFromLiveCheck, invalidateAccommodationQueries } from './accommodationQueryCache';
import styles from './ChangeRoomDrawer.module.css';
import { ACCOMMODATION_DRAWERS_STALE_TIME_MS } from './usePrefetchAccommodationDrawersOnInView';
type ChangeRoomDrawerProps = {
  isOpen: boolean;
  close: () => void;
  tripId: string;
  itemId: number;
  roomIndex: number;
  placeName: string;
};
export const BOARD_BASIS_ICON = {
  RO: Bed,
  BB: CoffeeCup,
  HB: StarHalfDashed,
  FB: Star,
  AI: ThreeStars
} as const;
export const ChangeRoomDrawer = ({
  isOpen,
  close,
  tripId,
  itemId,
  roomIndex,
  placeName
}: Readonly<ChangeRoomDrawerProps>) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const tCommon = useTranslations('common');
  const tForms = useTranslations('forms');
  const toast = useToast();
  const router = useRouter();
  const utils = api.useUtils();
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  const isDesktop = useIsDesktop();
  const sortOptions = [{
    id: 'price-asc',
    label: t('sort_price_asc')
  }, {
    id: 'price-desc',
    label: t('sort_price_desc')
  }, {
    id: 'name-asc',
    label: t('sort_name_asc')
  }, {
    id: 'name-desc',
    label: t('sort_name_desc')
  }] as const satisfies ReadonlyArray<{
    id: SortKeys;
    label: string;
  }>;
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortKeys>();
  const [selectedBoardBasis, setSelectedBoardBasis] = useState(new Set<Key>([]));
  const [isConfirming, setIsConfirming] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setSelectedRoomId(null);
      setSortBy(undefined);
      setSelectedBoardBasis(new Set<Key>([]));
    }
  }, [isOpen]);
  const changeRooms = api.trip.changeRooms.useMutation();
  const isTripUpdating = useIsTripUpdating();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const {
    data: mixes,
    isLoading
  } = api.trip.getAlternativeRoomsForStayItem.useQuery({
    journeyId: tripId,
    itemId,
    deduplicated: true
  }, {
    ...TRPC_SETTINGS.QUERY_NO_REFETCH,
    enabled: isOpen,
    staleTime: ACCOMMODATION_DRAWERS_STALE_TIME_MS
  });
  const {
    data: overview
  } = api.trip.getOverview.useQuery({
    tripId
  }, {
    ...TRPC_SETTINGS.QUERY_NO_REFETCH,
    enabled: !!tripId
  });
  const {
    adultCount,
    childAges
  } = aggregatePax(overview?.pax?.travellerMixes ?? []);
  const mix = mixes?.[roomIndex];
  const currentRoomId = mix?.currentSelectedRoom.id;
  const availableRooms: Room[] = mix?.availableRooms.map(entry => ({
    ...entry.room,
    priceDifference: entry.priceDifference
  })) ?? [];
  const sortedRooms = sortAndFilterRooms(availableRooms, {
    sortBy,
    selectedBoardBasis
  });
  const availableBoardBasis = new Set(availableRooms.map(room => sanitzeBoardBasis(room.boardBasis)));
  const extraBoardBasis = [...availableBoardBasis].filter(code => !(code in BOARD_BASIS_ICON)).sort((a, b) => a.localeCompare(b));
  const boardBasisOptions = [...Object.keys(BOARD_BASIS_ICON).filter(code_0 => availableBoardBasis.has(code_0)), ...extraBoardBasis];
  const effectiveSelectedId = selectedRoomId ?? currentRoomId;
  const selectedRoom = availableRooms.find(room_0 => room_0.id === effectiveSelectedId) ?? mix?.currentSelectedRoom;
  const hasChange = effectiveSelectedId !== currentRoomId;
  const totalChangeValue = hasChange ? selectedRoom?.priceDifference?.value ?? 0 : 0;
  const formatPerPerson = (value: number) => `${value > 0 ? '+' : ''}${convertPriceInGBP(getPricePerPerson(value, adultCount, childAges))}/pp`;
  const getRoomPriceDisplay = (room_1: Room) => {
    if (room_1.id === currentRoomId) {
      return formatPerPerson(0);
    }
    return formatPerPerson(room_1.priceDifference?.value ?? 0);
  };
  const confirmRoomChange = async () => {
    if (!mixes || !selectedRoom || isTripUpdating) return;
    try {
      trackFunnelEvent('trip_funnel_select_room');
      startLoader({
        showProgressBar: false
      });
      const specificRoomChangeDetails = mixes.map((entry_0, index) => {
        const room_2 = index === roomIndex ? selectedRoom : entry_0.currentSelectedRoom;
        return {
          travellerMixPosition: index,
          boardBasis: room_2.boardBasis,
          roomCode: room_2.roomCode
        };
      });
      const {
        success
      } = await changeRooms.mutateAsync({
        journeyId: tripId,
        stayItemId: itemId,
        specificRoomChangeDetails
      });
      if (!success) {
        toast?.add({
          title: t('accommodation_change_room_error'),
          variant: 'error',
          position: 'middle'
        }, {
          timeout: 5000
        });
        return;
      }
      setIsConfirming(true);
      clearStayFromLiveCheck(utils, tripId, itemId);
      await invalidateAccommodationQueries(utils, tripId, itemId);
      router.refresh();
      close();
      toast?.add({
        title: t('accommodation_change_room_success'),
        variant: 'success',
        position: 'middle'
      }, {
        timeout: 5000
      });
    } catch {
      logError('Error changing room');
      toast?.add({
        title: t('accommodation_change_room_error'),
        variant: 'error',
        position: 'middle'
      }, {
        timeout: 5000
      });
    } finally {
      setIsConfirming(false);
      completeProgressBar();
    }
  };
  const ariaLabel = t('accommodation_change_rooms_aria', {
    placeName
  });
  const boardBasisFilters = boardBasisOptions.length > 1 ? <ToggleButtonGroup selectionMode="multiple" selectedKeys={selectedBoardBasis} onSelectionChange={setSelectedBoardBasis}>
        {boardBasisOptions.map(boardBasis => {
      const Icon = BOARD_BASIS_ICON[boardBasis as keyof typeof BOARD_BASIS_ICON] ?? Bed;
      return <ToggleButton key={boardBasis} id={boardBasis} variant="pill" showToggleIndicator={false}>
              <Icon size="small" decorative />
              {expandedBoardBasis(t, boardBasis)}
            </ToggleButton>;
    })}
      </ToggleButtonGroup> : null;
  const sortSelect = <AriaSelect items={sortOptions} ariaLabel={tAria('sort_by')} onSelectionChange={selected => setSortBy(selected as SortKeys)} selectedKey={sortBy} variant="pill">
      {item => <SelectItem key={item.id}>{item.label}</SelectItem>}
    </AriaSelect>;
  const roomList = <ul className={styles.list} aria-label={tAria('available_rooms')}>
      {sortedRooms.map(room_3 => <li key={room_3.id}>
          <button type="button" className={styles.roomItem} aria-pressed={room_3.id === effectiveSelectedId} onClick={() => setSelectedRoomId(room_3.id)}>
            <div className={styles.roomInfo}>
              <p className={styles.roomName}>{room_3.displayName ?? room_3.roomName}</p>
              <p className={styles.roomBoardBasis}>{expandedBoardBasis(t, room_3.boardBasis)}</p>
            </div>
            <span className={styles.roomPrice}>{getRoomPriceDisplay(room_3)}</span>
          </button>
        </li>)}
    </ul>;
  const footerButtons = <div className={clsx(styles.footerButtons, isDesktop && styles.footerButtonsModal)}>
      <Button variant="secondary" size="small" isPillShape onPress={close}>
        {tForms('cancel')}
      </Button>
      <Button variant="primary" isPillShape size="small" isDisabled={!hasChange || isTripUpdating} isLoading={changeRooms.isPending || isConfirming} onPress={confirmRoomChange}>
        {t('confirm')}
      </Button>
    </div>;
  const hasNoRoomsToShow = isLoading || sortedRooms.length === 0;
  const roomsPlaceholder = isLoading ? <div className={styles.modalContentLoading} aria-busy="true" aria-live="polite">
      <span className="sr-only">{tCommon('loading')}</span>
      <div className={styles.loadingBar} />
    </div> : <p className={clsx(styles.empty, isDesktop && styles.emptyModal)}>{t('accommodation_rooms_empty')}</p>;
  const roomsContent = hasNoRoomsToShow ? roomsPlaceholder : roomList;
  const drawerBody = isDesktop ? <div className={clsx(styles.drawerBody, styles.drawerBodyModal)}>
      <h2 className={clsx(styles.heading, styles.headingModal)}>{t('accommodation_change_rooms_heading')}</h2>
      <Divider variant="horizontal" className={styles.dividerInset} />
      <div className={styles.filtersRowModal}>
        {boardBasisFilters}
        {sortSelect}
      </div>
      <Divider variant="horizontal" className={styles.dividerInset} />
      <div className={styles.modalMain}>
        <div className={styles.scrollAreaModal}>{roomsContent}</div>
        {hasNoRoomsToShow ? null : <div className={styles.footerModal}>
            <p className={styles.totalChangeModal}>
              {t('accommodation_rooms_total_change', {
            price: formatPerPerson(totalChangeValue)
          })}
            </p>
            {footerButtons}
          </div>}
      </div>
    </div> : <div className={styles.drawerBody}>
      <h2 className={styles.heading}>{t('accommodation_change_rooms_heading')}</h2>

      <div className={styles.filtersRow}>
        {boardBasisFilters}
        {sortSelect}
      </div>

      {hasNoRoomsToShow ? null : <p className={styles.resultsCount}>{t('room_options_summary', {
        count: sortedRooms.length
      })}</p>}

      <div className={styles.filtersDivider}>
        <Divider variant="horizontal" />
      </div>

      {hasNoRoomsToShow ? roomsPlaceholder : <>
          {roomList}
          <div className={styles.footer}>
            <p className={styles.totalChange}>
              {t('accommodation_rooms_total_change', {
            price: formatPerPerson(totalChangeValue)
          })}
            </p>
            {footerButtons}
          </div>
        </>}
    </div>;
  return isDesktop ? <CenteredModal ariaLabel={ariaLabel} close={close} closeDataTestId="ChangeRoomModalClose" dataTestId="ChangeRoomModal" isOpen={isOpen} size="large">
      {drawerBody}
    </CenteredModal> : <OverlayDrawer isOpen={isOpen} close={close} ariaLabel={ariaLabel} dataTestId="ChangeRoomDrawer">
      {drawerBody}
    </OverlayDrawer>;
};